"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCommunication } from "@/contexts/chat-provider";
import { useDebounce } from "@/hooks/use-debounce";
import { getActiveRecipient } from "@/lib/chat-utils";
import { MessageKind } from "@/types/enums/message";
import { ChatProvider } from "./chat-context";
import { CHAT_CHANNELS, ChatChannel, ChatMessagesHandle, ChatModalsHandle } from "./contexts";
import { useChatData, useChatDraft, useChatFiles, useMarkChatReadOnOpen, useSuggestionsList } from "./hooks";
interface ChatRootProps {
  chatId: string | null | undefined;
  size?: "sm" | "lg";
  showSuggestions?: boolean;
  onMinimize?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
}
export function ChatRoot(t0) {
  const $ = _c(62);
  const {
    chatId,
    size: t1,
    showSuggestions: t2,
    onMinimize,
    onClose,
    children
  } = t0;
  const size = t1 === undefined ? "lg" : t1;
  const showSuggestions = t2 === undefined ? true : t2;
  const {
    minimizeChat,
    closeChat
  } = useCommunication();
  const chatModalsRef = useRef(null);
  const chatMessagesRef = useRef(null);
  let t3;
  if ($[0] !== chatId) {
    t3 = {
      chatId
    };
    $[0] = chatId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    chat,
    chatDetail
  } = useChatData(t3);
  let t4;
  if ($[2] !== chatDetail || $[3] !== chatId) {
    t4 = {
      chatId,
      chat: chatDetail
    };
    $[2] = chatDetail;
    $[3] = chatId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useMarkChatReadOnOpen(t4);
  let t5;
  if ($[5] !== chatId) {
    t5 = {
      chatId
    };
    $[5] = chatId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    draftContent,
    draft,
    handleDraftChange,
    clearDraft
  } = useChatDraft(t5);
  let t6;
  if ($[7] !== chatId) {
    t6 = {
      chatId
    };
    $[7] = chatId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    selectedFiles,
    addFiles,
    removeFile,
    handleFileChange,
    previewFile,
    setPreviewFile,
    previewFileUrl,
    clearFiles
  } = useChatFiles(t6);
  let t7;
  if ($[9] !== chat) {
    t7 = getActiveRecipient(chat)?.id;
    $[9] = chat;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = draft?.message.kind === MessageKind.EVENT ? draft.message.eventId : undefined;
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = {
      primaryPatientId: t7,
      eventId: t8
    };
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    suggestions
  } = useSuggestionsList(t9);
  let t10;
  bb0: {
    if (onMinimize) {
      t10 = onMinimize;
      break bb0;
    }
    let t11;
    if ($[14] !== chatId || $[15] !== minimizeChat) {
      t11 = chatId ? () => minimizeChat(chatId) : undefined;
      $[14] = chatId;
      $[15] = minimizeChat;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t10 = t11;
  }
  const handleMinimize = t10;
  let t11;
  bb1: {
    if (onClose) {
      t11 = onClose;
      break bb1;
    }
    let t12;
    if ($[17] !== chatId || $[18] !== closeChat) {
      t12 = chatId ? () => closeChat(chatId) : undefined;
      $[17] = chatId;
      $[18] = closeChat;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    t11 = t12;
  }
  const handleClose = t11;
  const effectiveShowSuggestions = showSuggestions;
  const t12 = chatId ?? "";
  let t13;
  if ($[20] !== effectiveShowSuggestions || $[21] !== handleClose || $[22] !== handleMinimize || $[23] !== size || $[24] !== t12) {
    t13 = {
      chatId: t12,
      size,
      showSuggestions: effectiveShowSuggestions,
      onMinimize: handleMinimize,
      onClose: handleClose
    };
    $[20] = effectiveShowSuggestions;
    $[21] = handleClose;
    $[22] = handleMinimize;
    $[23] = size;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const configValue = t13;
  let t14;
  if ($[26] !== addFiles || $[27] !== clearDraft || $[28] !== clearFiles || $[29] !== draft || $[30] !== draftContent || $[31] !== handleDraftChange || $[32] !== handleFileChange || $[33] !== previewFile || $[34] !== previewFileUrl || $[35] !== removeFile || $[36] !== selectedFiles || $[37] !== setPreviewFile) {
    t14 = {
      draftContent,
      draft,
      handleDraftChange,
      clearDraft,
      selectedFiles,
      addFiles,
      removeFile,
      handleFileChange,
      previewFile,
      setPreviewFile,
      previewFileUrl,
      clearFiles
    };
    $[26] = addFiles;
    $[27] = clearDraft;
    $[28] = clearFiles;
    $[29] = draft;
    $[30] = draftContent;
    $[31] = handleDraftChange;
    $[32] = handleFileChange;
    $[33] = previewFile;
    $[34] = previewFileUrl;
    $[35] = removeFile;
    $[36] = selectedFiles;
    $[37] = setPreviewFile;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const inputValue = t14;
  const [channels, setChannels] = useState(_temp);
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = channel => {
      setChannels(current => {
        const next = new Set(current);
        if (next.has(channel)) {
          next.delete(channel);
        } else {
          next.add(channel);
        }
        return next.size === CHAT_CHANNELS.length ? new Set() : next;
      });
    };
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const toggleChannel = t15;
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setChannels(_temp2);
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const clearChannels = t16;
  const [search, setSearch] = useState("");
  let t17;
  if ($[41] !== search) {
    t17 = search.trim();
    $[41] = search;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const searchQuery = useDebounce(t17, 300);
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      clearChannels();
      setSearch("");
    };
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const clearFilters = t18;
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      clearFilters();
    };
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== chatId) {
    t20 = [chatId, clearFilters];
    $[45] = chatId;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  useEffect(t19, t20);
  const t21 = channels.size > 0 || !!searchQuery;
  let t22;
  if ($[47] !== channels || $[48] !== search || $[49] !== searchQuery || $[50] !== t21) {
    t22 = {
      channels,
      toggleChannel,
      clearChannels,
      search,
      setSearch,
      searchQuery,
      isFiltered: t21,
      clearFilters
    };
    $[47] = channels;
    $[48] = search;
    $[49] = searchQuery;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  const filterValue = t22;
  let t23;
  if ($[52] !== suggestions) {
    t23 = {
      suggestions
    };
    $[52] = suggestions;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const suggestionsValue = t23;
  let t24;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      chatModalsRef
    };
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const modalsRefValue = t24;
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      chatMessagesRef
    };
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const scrollRefValue = t25;
  let t26;
  if ($[56] !== children || $[57] !== configValue || $[58] !== filterValue || $[59] !== inputValue || $[60] !== suggestionsValue) {
    t26 = <ChatProvider configValue={configValue} inputValue={inputValue} suggestionsValue={suggestionsValue} filterValue={filterValue} modalsRefValue={modalsRefValue} scrollRefValue={scrollRefValue}>{children}</ChatProvider>;
    $[56] = children;
    $[57] = configValue;
    $[58] = filterValue;
    $[59] = inputValue;
    $[60] = suggestionsValue;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  return t26;
}
function _temp2(current_0) {
  return current_0.size === 0 ? current_0 : new Set();
}
function _temp() {
  return new Set();
}